import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { getCalendarEvents, hasEventsOnDate, type CalendarEvent } from '@utils/calendarEvents';
import { getMyCompany, type MyCompanyResponse } from '@services/companies';
import { isCompanyWorkingDay } from '@utils/companySchedule';
import { useI18n } from '../i18n';

export default function MiniCalendar() {
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const navigate = useNavigate();
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const [events, setEvents] = useState<CalendarEvent[]>([]);
	const [companyWorkingDays, setCompanyWorkingDays] = useState<number[] | null>(null);
	const [companyNonWorkingDates, setCompanyNonWorkingDates] = useState<Array<{ date: string; label?: string | null }>>([]);

	if (!user) return null;

	useEffect(() => {
		// Mini calendar dots always reflect "Only me" scope.
		// This keeps the sidebar consistent and avoids showing company/project-wide dots
		// when the user just wants their own schedule.
		const scope: 'self' = 'self';
		getCalendarEvents(user.role, user.id, scope)
			.then(setEvents)
			.catch(err => {
				console.error('Failed to load calendar events:', err);
				setEvents([]);
			});
	}, [user.role, user.id]);

	// Load company working days + non-working dates so mini calendar can hide dots on non-working days.
	useEffect(() => {
		let cancelled = false;
		const loadSchedule = async () => {
			if (!user?.id) return;
			if (user.role !== 'company' && user.role !== 'gardener') {
				if (!cancelled) {
					setCompanyWorkingDays(null);
					setCompanyNonWorkingDates([]);
				}
				return;
			}
			try {
				const myCompany: MyCompanyResponse = await getMyCompany(user.id);
				if (cancelled) return;
				const wd = (myCompany.company as any)?.working_days;
				const nwd = (myCompany.company as any)?.non_working_dates;
				setCompanyWorkingDays(Array.isArray(wd) && wd.length > 0 ? wd : null);
				setCompanyNonWorkingDates(Array.isArray(nwd) ? nwd : []);
			} catch {
				if (!cancelled) {
					setCompanyWorkingDays(null);
					setCompanyNonWorkingDates([]);
				}
			}
		};
		loadSchedule();
		return () => {
			cancelled = true;
		};
	}, [user?.id, user?.role]);

	const monthStart = useMemo(() => {
		const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
		return date;
	}, [currentMonth]);

	const monthEnd = useMemo(() => {
		const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
		return date;
	}, [currentMonth]);

	const startDate = useMemo(() => {
		const start = new Date(monthStart);
		const day = start.getDay();
		const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
		return new Date(start.setDate(diff));
	}, [monthStart]);

	const calendarDays = useMemo(() => {
		const days: Date[] = [];
		const current = new Date(startDate);
		
		for (let i = 0; i < 42; i++) {
			days.push(new Date(current));
			current.setDate(current.getDate() + 1);
		}
		
		return days;
	}, [startDate]);

	const monthName = currentMonth.toLocaleDateString(
		language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB',
		{ month: 'long', year: 'numeric' }
	);

	const handlePrevMonth = () => {
		setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
	};

	const handleNextMonth = () => {
		setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
	};

	const handleDateClick = () => {
		navigate('/app/calendar');
	};

	const isToday = (date: Date) => {
		const today = new Date();
		return date.toDateString() === today.toDateString();
	};

	const isCurrentMonth = (date: Date) => {
		return date.getMonth() === currentMonth.getMonth();
	};

	const filteredEvents = useMemo(() => {
		// Hide task dots on non-working days (weekends/closed days).
		const workingDays = companyWorkingDays ?? [1, 2, 3, 4, 5];
		const nonWorking = companyNonWorkingDates ?? [];
		return events.filter((e) => {
			if (e.type !== 'task') return true;
			return isCompanyWorkingDay(e.date, workingDays, nonWorking);
		});
	}, [events, companyWorkingDays, companyNonWorkingDates]);

	return (
		<div className="mini-calendar" onClick={handleDateClick} style={{ cursor: 'pointer' }}>
			<div className="d-flex justify-content-between align-items-center mb-2">
				<button
					type="button"
					className="btn btn-sm btn-link p-0 text-muted"
					onClick={(e) => {
						e.stopPropagation();
						handlePrevMonth();
					}}
					style={{ fontSize: '0.75rem' }}
				>
					<i className="bi bi-chevron-left"></i>
				</button>
				<div className="small fw-semibold text-center" style={{ fontSize: '0.75rem', flex: 1 }}>
					{monthName}
				</div>
				<button
					type="button"
					className="btn btn-sm btn-link p-0 text-muted"
					onClick={(e) => {
						e.stopPropagation();
						handleNextMonth();
					}}
					style={{ fontSize: '0.75rem' }}
				>
					<i className="bi bi-chevron-right"></i>
				</button>
			</div>
			
			<div className="mini-calendar-grid">
				{[
					copy('Mon', 'Mo', 'Po'),
					copy('Tue', 'Di', 'Ut'),
					copy('Wed', 'Mi', 'Sr'),
					copy('Thu', 'Do', 'Čet'),
					copy('Fri', 'Fr', 'Pe'),
					copy('Sat', 'Sa', 'Su'),
					copy('Sun', 'So', 'Ne')
				].map(day => (
					<div key={day} className="mini-calendar-day-header text-muted" style={{ fontSize: '0.65rem', fontWeight: 600 }}>
						{day.slice(0, 1)}
					</div>
				))}
				
				{calendarDays.map((date, idx) => {
					const hasEvents = hasEventsOnDate(filteredEvents, date);
					const isCurrent = isCurrentMonth(date);
					const isTodayDate = isToday(date);
					
					return (
						<div
							key={idx}
							className={`mini-calendar-day ${!isCurrent ? 'text-muted' : ''} ${isTodayDate ? 'today' : ''}`}
							style={{
								fontSize: '0.7rem',
								position: 'relative',
								opacity: isCurrent ? 1 : 0.4
							}}
						>
							{date.getDate()}
							{hasEvents && (
								<span
									className="mini-calendar-event-dot"
									style={{
										position: 'absolute',
										bottom: '2px',
										left: '50%',
										transform: 'translateX(-50%)',
										width: '4px',
										height: '4px',
										borderRadius: '50%',
										backgroundColor: '#2f9e59'
									}}
								></span>
							)}
						</div>
					);
				})}
			</div>
			
			<div className="text-center mt-2">
				<small className="text-muted" style={{ fontSize: '0.7rem' }}>
					{copy('Click to view full calendar', 'Klicken, um den ganzen Kalender zu öffnen', 'Kliknite za prikaz cijelog kalendara')}
				</small>
			</div>
		</div>
	);
}

